// src/encounter/lab-add-parts.tsx
// The Add lab popup's header pieces (PROD-2640), from canvas "Lab Panels and Tests" artboards 6-9:
//   · LabHeaderAside — the right side of the LABS header row: a kind chip (blue for CPT and lab
//     panels, grey for house panels and tests), "Performing lab: <vendor>", and a Change link (6.3);
//   · LabChipRow — the chip row between the header and DIAGNOSIS: specimen chips (grey, droplet)
//     and, for a CPT panel, the blue "Bills as <CPT>" chip (6.5 / 6.7). Container, volume and prep
//     chips (6.6) wait on Master (P-B2);
//   · LabBillTo — the BILL TO Patient / Clinic control (6.13 / 7.10), defaulting to Clinic for
//     every vendor; the popup sends it as `billTo` (Scrips.Patient #1000, P-B6).
//
// ICONS: src/encounter is lucide-free (PROD-1599). The DS has no Droplet or Tag glyph, so the
// specimen chip uses ScripsIcon `lab` and the "Bills as" chip ScripsIcon `billing` (same meaning,
// different drawing). A DS gap, flagged in the PR.
import { useRef, useState } from 'react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverItem,
  AnchoredPopoverTrigger,
  Segmented,
} from '@scripsteam/scrips-signal-ds';
import { FlaskConical, Receipt } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AP_FIELD_LABEL_CLASS } from './ap-dialog-label-style';
import { billToHint, type LabBillToValue } from './lib/lab-bill-to';
import { useDesignV2 } from './lib/design-v2';

// ── Header aside ────────────────────────────────────────────────────────────────────────────────

const ASIDE = 'relative inline-flex items-center gap-2';
// 16px tall, 4px radius, 12px/500 — the design's kind chip.
const KIND_CHIP = 'inline-flex h-4 items-center justify-center whitespace-nowrap rounded-sm px-1 text-size-eyebrow font-medium';
const KIND_TONE = {
  blue: 'bg-blue-100 text-brand-depth',
  grey: 'bg-status-neutral-bg text-grey-800',
} as const;
/** Design sheet v1.1 (switch ON only): the lab kind is a category, so it is a neutral tag — no brand
 *  tint as category identity (R7). */
const KIND_TONE_V2 = {
  blue: 'bg-status-neutral-bg text-grey-800',
  grey: 'bg-status-neutral-bg text-grey-800',
} as const;
const PERFORMING = 'text-size-eyebrow text-text-secondary whitespace-nowrap';
const CHANGE =
  'border-0 bg-transparent p-0 font-sans text-size-eyebrow font-medium text-brand-primary cursor-pointer';

export interface LabVendorChoice {
  id: string;
  name: string;
}

export interface LabHeaderAsideProps {
  /** "CPT panel" · "Lab panel" · "House panel" · "Test". Absent → no chip. */
  kind?: { label: string; tone: 'blue' | 'grey' };
  /** The performing lab's name; absent → "Not set". */
  vendorName?: string;
  /** The clinic's active vendors, for Change. Empty → no Change link. */
  vendors: readonly LabVendorChoice[];
  onPickVendor: (id: string) => void;
}

export function LabHeaderAside({ kind, vendorName, vendors, onPickVendor }: LabHeaderAsideProps) {
  const [open, setOpen] = useState(false);
  const designV2 = useDesignV2();
  // The popover renders in place (not portalled to <body>) so it stays inside the modal's focus trap.
  const hostRef = useRef<HTMLSpanElement>(null);
  return (
    <span ref={hostRef} className={ASIDE} data-testid="lab-vendor">
      {kind && <span className={cn(KIND_CHIP, (designV2 ? KIND_TONE_V2 : KIND_TONE)[kind.tone])}>{kind.label}</span>}
      <span className={PERFORMING}>
        Performing lab:{' '}
        <strong className="font-semibold text-text-primary">{vendorName || 'Not set'}</strong>
      </span>
      {vendors.length > 0 && (
        <AnchoredPopover open={open} onOpenChange={setOpen}>
          <AnchoredPopoverTrigger
            render={
              <button type="button" className={CHANGE}>
                Change
              </button>
            }
          />
          <AnchoredPopoverContent container={hostRef} align="end" aria-label="Performing lab">
            {vendors.map((v) => (
              <AnchoredPopoverItem
                key={v.id}
                onClick={() => {
                  onPickVendor(v.id);
                  setOpen(false);
                }}
              >
                {v.name}
              </AnchoredPopoverItem>
            ))}
          </AnchoredPopoverContent>
        </AnchoredPopover>
      )}
    </span>
  );
}

// ── Chip row ────────────────────────────────────────────────────────────────────────────────────

const CHIP_ROW = 'flex flex-wrap gap-2';
// 24px tall, 6px radius, 12px/500, 16px icon (the icon scale's inline step).
const CHIP = 'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-control px-2 text-size-eyebrow font-medium';
const CHIP_GREY = 'bg-grey-200 text-text-secondary';
const CHIP_BLUE = 'bg-blue-100/70 text-brand-depth';

export function LabChipRow({ specimens, billsAs }: { specimens: readonly string[]; billsAs?: string | null }) {
  if (specimens.length === 0 && !billsAs) return null;
  return (
    <div className={CHIP_ROW} data-testid="lab-chips">
      {specimens.map((s) => (
        <span key={s} className={cn(CHIP, CHIP_GREY)} data-chip="specimen">
          <FlaskConical className="size-4" aria-hidden />
          {s}
        </span>
      ))}
      {billsAs && (
        <span className={cn(CHIP, CHIP_BLUE)} data-chip="bills-as">
          <Receipt className="size-4" aria-hidden />
          {billsAs}
        </span>
      )}
    </div>
  );
}

// ── Bill to ─────────────────────────────────────────────────────────────────────────────────────

const BILL_TO_ROW = 'flex items-center justify-between gap-4';
const BILL_TO_HINT = 'mt-2 text-size-eyebrow leading-body text-text-muted';

export function LabBillTo({
  vendorName,
  value,
  onChange,
}: {
  vendorName?: string;
  value: LabBillToValue;
  onChange: (v: LabBillToValue) => void;
}) {
  const hint = billToHint(vendorName, value);
  return (
    <div className={BILL_TO_ROW} data-testid="lab-bill-to">
      <div>
        <span className={AP_FIELD_LABEL_CLASS}>Bill to</span>
        <div className={BILL_TO_HINT}>
          {hint.lead}
          {hint.clinic && <span className="text-brand-depth"> {hint.clinic}</span>}
        </div>
      </div>
      <Segmented<LabBillToValue>
        variant="pill"
        aria-label="Bill to"
        value={value}
        onChange={onChange}
        options={[
          { value: 'Patient', label: 'Patient' },
          { value: 'Clinic', label: 'Clinic' },
        ]}
      />
    </div>
  );
}
